import { inject, Injectable } from '@angular/core';
import {
	BusinessHourDTO,
	BusinessHoursOverview,
	DailyBusinessHour,
	dayI18nKeys,
	DayKey,
	orderedDays,
	TimeParts,
} from '../models/callback-on-callnote.model';
import { I18nKeys } from '../models/i18n.model';
import dayjs from 'dayjs';
import { SERVER_TIME_ZONE } from '../constants/time-zone';
import { I18nPipe } from '../pipe/i18n-pipe';
import {
	timeIsOutsideOfTimeRange,
	timeIsOutsideOfTimeRangeOrWeekendDay,
} from '../helpers/date-time.helper';

@Injectable({
	providedIn: 'root',
})
export class BusinessHoursService {
	private readonly i18nPipe: I18nPipe = inject(I18nPipe);
	private _timezone: string = SERVER_TIME_ZONE;
	private dailyBusinessHours: (DailyBusinessHour | null)[] = [];

	get timezone(): string {
		return this._timezone;
	}

	set timezone(value: string) {
		if (value) {
			this._timezone = value;
		}
	}

	public alignDateOnBusinessHoursIfRequired(date: Date): Date {
		const noBusinessDay = this.dailyBusinessHours[date.getDay()] === null;
		let noBusinessTimeRange = false;
		if (!noBusinessDay) {
			const day = this.dailyBusinessHours[date.getDay()]!;
			const startTime: TimeParts = { hour: day.startHour, minute: day.startMinute };
			const endTime: TimeParts = { hour: day.endHour, minute: day.endMinute };
			noBusinessTimeRange = timeIsOutsideOfTimeRange(date, startTime, endTime);
		}

		if (noBusinessDay || noBusinessTimeRange) {
			return this.getNextBusinessDate(date);
		}
		return date;
	}

	public getBusinessHoursOverview(businessHours: BusinessHourDTO[]): BusinessHoursOverview {
		const orderedBusinessHours: (null | DailyBusinessHour)[] = orderedDays.map(() => null);

		(businessHours || []).forEach((businessHour: BusinessHourDTO) => {
			orderedDays.forEach((dayKey: DayKey, index: number) => {
				if (businessHour.day.charAt(index) === dayKey) {
					const startTime = this.getLocalTime(businessHour.start);
					const endTime = this.getLocalTime(businessHour.end);

					const dailyBusinessHour = new DailyBusinessHour(dayI18nKeys[index], startTime, endTime);
					const targetIndex = index === 6 ? 0 : index + 1;
					orderedBusinessHours[targetIndex] = dailyBusinessHour;
				}
			});
		});
		this.dailyBusinessHours = orderedBusinessHours;

		const optimizedInformation: string[] = [];
		(businessHours || []).forEach((businessHour: BusinessHourDTO) => {
			const startTime = this.getLocalTime(businessHour.start).format('HH:mm');
			const endTime = this.getLocalTime(businessHour.end).format('HH:mm');
			let days = '';
			let firstDayLabel;
			let lastDayLabel;
			for (let index = 0; index < orderedDays.length; index++) {
				if (businessHour.day.charAt(index) === orderedDays[index]) {
					if (!firstDayLabel) {
						firstDayLabel = dayI18nKeys[index];
					} else {
						lastDayLabel = dayI18nKeys[index];
					}
				} else if (!!firstDayLabel) {
					break;
				}
			}

			if (!!firstDayLabel) {
				days = this.i18nPipe.translate(firstDayLabel);
			}
			if (!!lastDayLabel && lastDayLabel !== firstDayLabel) {
				days = days.concat(`-${this.i18nPipe.translate(lastDayLabel)}`);
			}
			if (days.length > 0 && !!startTime && !!endTime) {
				optimizedInformation.push(`${days} : ${startTime} - ${endTime}`);
			}
		});
		const businessHourList: string[] = orderedBusinessHours
			.map((dailyBusinessHour: DailyBusinessHour | null) => {
				if (!dailyBusinessHour) {
					return '';
				}

				const label: string = this.i18nPipe.translate(dailyBusinessHour.label);
				return `${label}:   ${dailyBusinessHour.startTime} - ${dailyBusinessHour.endTime}`;
			})
			.filter((item) => item !== '');
		businessHourList.unshift(this.i18nPipe.translate(I18nKeys.BUSINESS_HOUR));

		return {
			dailyBusinessHour: orderedBusinessHours,
			optimizedInformation: optimizedInformation,
			tooltip: businessHourList.join('\n'),
		};
	}

	public getNextValidDateAndTime(dailyBusinessHour: (null | DailyBusinessHour)[]) {
		const nextValidDate = new Date();

		if (dailyBusinessHour[nextValidDate.getDay()]) {
			const validTimeForToday = this.getNextValidTimeForToday(
				dailyBusinessHour[nextValidDate.getDay()]!,
			);
			if (validTimeForToday) {
				nextValidDate.setHours(validTimeForToday!.hour, validTimeForToday!.minute);
				return nextValidDate;
			}
		}

		nextValidDate.setDate(nextValidDate.getDate() + 1);

		for (let i = 0; i < dailyBusinessHour.length; i++) {
			if (dailyBusinessHour[nextValidDate.getDay()]) {
				break;
			} else {
				nextValidDate.setDate(nextValidDate.getDate() + 1);
			}
		}

		if (dailyBusinessHour[nextValidDate.getDay()]) {
			nextValidDate.setHours(
				dailyBusinessHour[nextValidDate.getDay()]!.startHour,
				dailyBusinessHour[nextValidDate.getDay()]!.startMinute,
			);
		}

		return nextValidDate;
	}

	private getNextBusinessDate(date: Date): Date {
		const initialDate = dayjs(date);

		if (this.dailyBusinessHours[date.getDay()] !== null) {
			const businessHour = this.dailyBusinessHours[date.getDay()]!;
			const startTime = initialDate
				.clone()
				.hour(businessHour.startHour)
				.minute(businessHour.startMinute);
			if (initialDate.isBefore(startTime)) {
				return this.getNewDate(date, 0, businessHour);
			}
		}
		let addDays = 0;
		// start at the next day - because the previous check handled the same day but earlier situation - and after office hour will be the next day
		for (let dayIndex = date.getDay() + 1; dayIndex < this.dailyBusinessHours.length; dayIndex++) {
			addDays++;
			if (this.dailyBusinessHours[dayIndex] !== null) {
				return this.getNewDate(date, addDays, this.dailyBusinessHours[dayIndex]!);
			}
		}
		for (let dayIndex = 0; dayIndex <= date.getDay(); dayIndex++) {
			addDays++;
			if (this.dailyBusinessHours[dayIndex] !== null) {
				return this.getNewDate(date, addDays, this.dailyBusinessHours[dayIndex]!);
			}
		}

		return date;
	}

	private getNewDate(date: Date, daysToAdd: number, businessHour: DailyBusinessHour): Date {
		let day = dayjs(date).add(daysToAdd, 'day');
		day = day.hour(businessHour.startHour).minute(businessHour.startMinute).second(0);
		return day.toDate();
	}

	private getNextValidTimeForToday({
		startHour,
		startMinute,
		endHour,
		endMinute,
	}: DailyBusinessHour) {
		const now = new Date();
		const next = new Date(now.getTime() + 30 * 60 * 1000);

		const nextHour = next.getHours();
		const nextMinute = next.getMinutes();

		// Convert all times to minutes since midnight for easy comparison
		const nextTotal = nextHour * 60 + nextMinute;
		const minTotal = startHour * 60 + startMinute;
		const maxTotal = endHour * 60 + endMinute;

		if (nextTotal < minTotal || nextTotal > maxTotal) {
			return null;
		}

		return {
			hour: nextHour,
			minute: nextMinute,
		};
	}

	private getLocalTime(time: string) {
		const today = dayjs().format('YYYY-MM-DD');
		const combined = `${today}T${time}`;
		const timezoneAwareDate = dayjs.tz(combined, 'YYYY-MM-DDTHH:mm:ss', this.timezone);
		return timezoneAwareDate.local();
	}
}
